export const ADMIN_VIEW_AS_USER_ID_SESSION_KEY = 'pm_admin_view_as_user_id';
export const ADMIN_VIEW_AS_HEADER = 'X-PM-Admin-Act-As-User-Id';

const BLOCKED_PATH_PREFIXES = [
  '/api/v1/admin',
  '/api/v1/auth',
  '/api/v1/dev',
  '/oauth',
  '/pm-admin-mcp',
  '/pm-mcp',
  '/pm-oauth-mcp',
  '/health',
];

function getStorage(): Storage | null {
  if (typeof window === 'undefined') {
    return null;
  }
  return window.sessionStorage;
}

// Keep UI decisions and request headers aligned while this tab cannot use storage.
// Successful reads remain authoritative; only failed mutations need a retry.
let currentUserId: string | null = null;
let pendingWrite = false;

function persistPendingDecision(): void {
  if (!pendingWrite) return;
  try {
    const storage = getStorage();
    if (!storage) return;
    if (currentUserId) storage.setItem(ADMIN_VIEW_AS_USER_ID_SESSION_KEY, currentUserId);
    else storage.removeItem(ADMIN_VIEW_AS_USER_ID_SESSION_KEY);
    pendingWrite = false;
  } catch {
    // Keep the explicit set/clear decision until storage accepts it.
  }
}

export function getAdminViewAsUserId(): string | null {
  if (pendingWrite) {
    persistPendingDecision();
    return currentUserId;
  }
  try {
    const storage = getStorage();
    if (storage) currentUserId = storage.getItem(ADMIN_VIEW_AS_USER_ID_SESSION_KEY)?.trim() || null;
  } catch {
    // A read failure must not silently remove the target from request headers.
  }
  return currentUserId;
}

export function setAdminViewAsUserId(userId: string): void {
  currentUserId = userId.trim() || null;
  pendingWrite = true;
  persistPendingDecision();
}

export function clearAdminViewAsUserId(): void {
  currentUserId = null;
  pendingWrite = true;
  persistPendingDecision();
}

function resolvePath(url: string): string {
  try {
    return new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://localhost').pathname;
  } catch {
    return url.startsWith('/') ? url : `/${url}`;
  }
}

export function shouldAttachAdminViewAsHeader(url: string): boolean {
  const path = resolvePath(url);
  return !BLOCKED_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));
}

export function getAdminViewAsHeaders(url: string): Record<string, string> {
  const userId = getAdminViewAsUserId();
  if (!userId || !shouldAttachAdminViewAsHeader(url)) {
    return {};
  }
  return { [ADMIN_VIEW_AS_HEADER]: userId };
}
